import { UPDATE_PAGINATION } from "@/constants/pagination";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createToast } from "vercel-toast";
import { CreateBookingApi } from "../../adapters/create-booking";
import { CreateBookingDataParams } from "../../adapters/create-booking/create-booking.types";
import { DeleteBookingApi } from "../../adapters/delete-booking-item";
import { DeleteBookingItemParams } from "../../adapters/delete-booking-item/delete-booking-item.types";
import { GetBookingsApi } from "../../adapters/get-bookings";
import { BookingDetails } from "../../adapters/get-bookings/get-bookings.types";
import { GetPrivateRoomsBySpaceApi } from "../../adapters/get-privaterooms-by-space";
import { useGetLeagues } from "../../hooks/use-get-leagues";
import { useToast } from "@/components/ui/use-toast";

import { BOOKING_TYPE } from "@/constants/booking-type";
import { useFilters } from "@/hooks/use-filters";
import { getContentByName } from "@/utils/get-content-by-name";
import { format, isBefore, startOfMonth } from "date-fns";
import { UpdateBookingApi } from "../../adapters/update-booking";
import { UpdateBookingDataParams } from "../../adapters/update-booking/update-booking.types";
import { FilterBookingFormData } from "../../layouts/filters-booking/filters-booking.types";
// import { useGetSpaces } from "../use-get-spaces";
import { bookingFiltersData } from "./use-booking.types";
import { CreateUpSellDataParams } from "../../adapters/create-upsell/create-upsell.types";
import { CreateUpSellAPi } from "../../adapters/create-upsell";
import {
  UpdatePoBookingApi,
  UpdatePoBookingParams,
} from "../../adapters/update-po-booking";

export const useBooking = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const [activePrivateRoom, setActivePrivateRoom] = useState(false);
  const [bookings, setBookings] = useState<BookingDetails[]>();
  const [selectedItem, setSelectedItem] = useState<BookingDetails | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [qtdBookings, setQtdBookings] = useState<number>();
  const [bookingId, setBookingId] = useState<number>();
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);

  // const { data: spaces } = useGetSpaces();

  const [privateRooms, setPrivateRooms] = useState<
    { id: number; name: string }[]
  >([]);

  const [activePage, setActivePage] = useState(
    searchParams.get("page") ? Number(searchParams.get("page")) : 1,
  );
  const { data: leagues } = useGetLeagues();

  const navigate = useNavigate();

  const [totalPages, setTotalPages] = useState(0);
  const [perPage] = useState(10);

  const { setFiltersDates } = useFilters();

  const [filters, setFilters] = useState<bookingFiltersData>({
    start_date: setFiltersDates(startOfMonth(new Date())),
    end_date: setFiltersDates(
      startOfMonth(new Date(new Date().setMonth(new Date().getMonth() + 2))),
    ),
  });

  const handleBooking = async (page: number, poContractId?: number) => {
    const bookingFilters = {
      types: filters.types || [
        BOOKING_TYPE.PRIVATE_OFFICE_CROSS_PRODUCT,
        BOOKING_TYPE.EXTRA_SERVICE,
      ],
      limit: perPage,
      page: page,
      offset:
        filters.offset || filters.offset === 0
          ? filters.offset
          : (page - 1) * perPage,
      others: {
        ...filters,
        ...(poContractId !== undefined && { po_contract_id: poContractId }),
      },
    };

    const { total, bookings, totalPages } =
      await GetBookingsApi(bookingFilters);

    const bookings_formatted = bookings.map((item: any) => {
      const company_name = leagues
        .filter((league) => league.id === item.company_id)
        .map((item) => item.name)[0];

      return {
        ...item,
        company_name,
        bookingAttributes: {
          booking_commission_fee: getContentByName(
            item.bookingAttributes,
            "booking_commission_fee",
          ),
          booking_payout: getContentByName(
            item.bookingAttributes,
            "booking_payout",
          ),
          booking_payout_rate_perc: getContentByName(
            item.bookingAttributes,
            "booking_payout_rate_perc",
          ),
        },
      };
    });

    const countPages = totalPages;

    if (countPages === 1 && page > 1) {
      handleChangePage(1);
      setActivePage(1);
    } else {
      setActivePage(page);
    }
    setTotalPages(countPages);

    setQtdBookings(total);
    setBookings([...bookings_formatted]);
  };

  const getAllBookings = async (otherFilters: bookingFiltersData) => {
    const bookingFilters = {
      types: otherFilters.types || [
        BOOKING_TYPE.PRIVATE_OFFICE_CROSS_PRODUCT,
        BOOKING_TYPE.EXTRA_SERVICE,
      ],
      others: otherFilters,
    };

    const { bookings } = await GetBookingsApi(bookingFilters);

    const bookings_formatted = bookings.map((item: any) => {
      const company_name = leagues
        .filter((league) => league.id === item.company_id)
        .map((item) => item.name)[0];

      return {
        ...item,
        company_name,
        bookingAttributes: {
          booking_commission_fee: getContentByName(
            item.bookingAttributes,
            "booking_commission_fee",
          ),
          booking_payout: getContentByName(
            item.bookingAttributes,
            "booking_payout",
          ),
          booking_payout_rate_perc: getContentByName(
            item.bookingAttributes,
            "booking_payout_rate_perc",
          ),
        },
      };
    });

    return bookings_formatted;
  };

  const handleDeleteBooking = async (data: DeleteBookingItemParams) => {
    let result;

    try {
      if (data.cancel_date) {
        result = await DeleteBookingApi(data);
        handleCloseModal();
        setSelectedItem(null);
      }
    } catch (err) {
      setSelectedItem(null);
    }

    return result;
  };

  const handleSelectItem = (item: BookingDetails, detail: boolean) => {
    if (detail) {
      setOpenDetail(true);
    } else {
      setOpenDeleteModal(true);
      setOpenDetail(false);
    }
    setSelectedItem(item);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setOpenDeleteModal(false);
  };

  const PopulateBookings = async (page: number, poContractId?: number) => {
    try {
      await handleBooking(page, poContractId);

      setSearchParams({ page: String(page) });
    } catch (err) {
      console.log({ err });
    }
  };

  const updateActivePage = (page: number) => {
    setActivePage(page);
  };

  const handleChangePage = async (page: number) => {
    updateActivePage(page);
    PopulateBookings(page);
  };

  const changeActivePage = (update: UPDATE_PAGINATION) => {
    const operation =
      update === UPDATE_PAGINATION.INCREMENT ? activePage + 1 : activePage - 1;
    updateActivePage(operation);
    PopulateBookings(operation);
  };

  const decreasePage = () => {
    changeActivePage(UPDATE_PAGINATION.DECREMENT);
  };

  const increasePage = () => {
    changeActivePage(UPDATE_PAGINATION.INCREMENT);
  };

  async function handleCreateBooking(data: CreateBookingDataParams) {
    setLoadingStatus(true);

    const response = await CreateBookingApi({
      data,
    });

    setLoadingStatus(false);

    if (response?.data.success_list.length) {
      return true;
    }

    if (response?.data.failed_list.length) {
      return false;
    }
  }

  async function handleCreateUpsell(data: CreateUpSellDataParams) {
    setLoadingStatus(true);

    const response = await CreateUpSellAPi({
      data,
      navigate: navigate,
    });

    setLoadingStatus(false);
    return response;
  }

  async function handleUpdateBooking(data: UpdateBookingDataParams) {
    const result = await UpdateBookingApi({
      data,
    });

    if (result.data.success_list.length) {
      const today = new Date();
      handleDeleteBooking({ id: bookingId, cancel_date: today.toString() });
    } else if (result.data.failed_list.length) {
      createToast(result.data.failed_list[0].error.details[0].message, {
        type: "error",
        timeout: 2000,
      });
    }
  }

  async function handleUpdatePoBooking(data: UpdatePoBookingParams) {
    const result = await UpdatePoBookingApi(data);

    if (!result.success) {
      toast({
        title: "Algo deu errado",
        description: (
          <div>
            <span className="text-yellow-950 text-sm font-normal">
              Não foi possível fazer a edição.
              <br />
            </span>
            <span className="text-yellow-600 font-normal">
              Erro: {result.error}
            </span>
          </div>
        ),
        duration: 4000,
        className:
          "fixed top-24 bg-amber-50 right-4 w-[400px] border border-gray-200",
      });
      return;
    }

    toast({
      title: "Sucesso",
      description: "O booking foi editado.",
      duration: 4000,
      className: "fixed top-24 right-4 w-[400px] border border-gray-200",
    });
  }

  const handleSelectSpace = async (id: number) => {
    const { data: privateRoomsResponse } = await GetPrivateRoomsBySpaceApi({
      space_id: id,
    });

    setActivePrivateRoom(true);
    setPrivateRooms(privateRoomsResponse);

    if (privateRoomsResponse.length === 0) {
      createToast("Não existe espaços privados cadastrados nesses coworking", {
        type: "error",
        timeout: 2000,
      });
    }
  };

  const getBookingId = (bookingId: number) => {
    setBookingId(bookingId);
  };

  async function updateFilters(data: FilterBookingFormData) {
    const dataFilters: bookingFiltersData = {
      start_date: data.start_date,
      end_date: data.end_date,
      offset: 0,
    };

    if (
      isBefore(
        format(data.end_date, "yyyy-MM-dd"),
        format(data.start_date, "yyyy-MM-dd"),
      )
    ) {
      createToast("Data de início deve ser anterior a data final", {
        type: "error",
        timeout: 2000,
      });

      return;
    }

    if (data.cities && data.cities.name) {
      dataFilters.cities = [data.cities.name];
    }

    if (data.status && data.status.id) {
      dataFilters.status = [data.status.id];
    }

    if (data.companies && data.companies.id) {
      dataFilters.companies = [data.companies.id];
    }

    if (data.spaces && data.spaces.id) {
      dataFilters.spaces = [data.spaces.id];
    }

    if (data.po_contract_id) {
      dataFilters.po_contract_id = data.po_contract_id;
    }

    if (data.types && data.types.length > 0) {
      dataFilters.types = data.types;
    }

    setFilters(dataFilters);
  }

  return {
    leagues,
    bookings,
    handleCreateBooking,
    handleCreateUpsell,
    handleUpdateBooking,
    activePrivateRoom,
    handleSelectSpace,
    privateRooms,
    handleChangePage,
    activePage,
    increasePage,
    decreasePage,
    selectedItem,
    handleSelectItem,
    handleCloseModal,
    handleDeleteBooking,
    openDeleteModal,
    setOpenDetail,
    getBookingId,
    openDetail,
    qtdBookings,
    filters,
    totalPages,
    updateFilters,
    PopulateBookings,
    getAllBookings,
    loadingStatus,
    handleUpdatePoBooking,
  };
};
